import React from 'react';
import { Lock, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAccess } from '../contexts/AccessContext';
import { useLanguage } from '../contexts/LanguageContext';

// Badge component for locked features
export const LockedBadge = ({ size = 'sm' }) => {
  const sizes = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-sm px-2 py-1',
    lg: 'text-base px-3 py-1.5'
  };
  
  return (
    <span className={`inline-flex items-center gap-1 bg-amber-500/20 text-amber-400 rounded-full ${sizes[size]}`}>
      <Lock size={size === 'sm' ? 10 : size === 'md' ? 12 : 14} />
      PRO
    </span>
  );
};

// Wrapper component that shows lock overlay for locked features
export const FeatureGate = ({ 
  featureId, 
  children, 
  fallback = null,
  showOverlay = true,
  title = null,  // Optional custom title
  description = null  // Optional custom description 
}) => {
  const { hasFeatureAccess, loading } = useAccess();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  // Feature descriptions for locked state display (using translation keys)
  const FEATURE_INFO = {
    categories_on_air: {
      titleKey: "categoriesOnAirTitle",
      descKey: "categoriesOnAirDesc"
    },
    campaign_analyzer: {
      titleKey: "campaignAnalyzerTitleLong",
      descKey: "campaignAnalyzerDescLong"
    },
    listener_calculator: {
      titleKey: "listenerCalculatorTitleLong", 
      descKey: "listenerCalculatorDescLong"
    },
    radio_top_songs: {
      titleKey: "radioTopSongsTitleLong",
      descKey: "radioTopSongsDescLong"
    },
    monthly_support: {
      titleKey: "monthlySupportTitle",
      descKey: "monthlySupportDesc"
    },
    priority_support: {
      titleKey: "prioritySupportTitle",
      descKey: "prioritySupportDesc"
    },
    peoples_chart: {
      titleKey: "peoplesChartTitle",
      descKey: "peoplesChartDesc"
    },
    compare_stations: {
      titleKey: "compareStationsTitle",
      descKey: "compareStationsDesc"
    }
  };
  
  if (loading) {
    return (
      <div className="animate-pulse bg-gray-200 rounded-lg h-32"></div>
    );
  }
  
  const hasAccess = hasFeatureAccess(featureId);
  
  if (hasAccess) {
    return children;
  }
  
  if (fallback) {
    return fallback;
  }
  
  if (!showOverlay) {
    return null;
  }
  
  // Get feature info or use custom/default values
  const featureInfo = FEATURE_INFO[featureId] || {};
  const displayTitle = title || (featureInfo.titleKey ? t(featureInfo.titleKey) : t('lockedFeature'));
  const displayDescription = description || (featureInfo.descKey ? t(featureInfo.descKey) : t('upgradeForAccessFeature'));
  
  return (
    <div className="relative">
      {/* Blurred content */}
      <div className="blur-sm pointer-events-none select-none opacity-50">
        {children}
      </div>
      
      {/* Lock overlay with feature info */}
      <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-[3px] rounded-xl">
        <div className="text-center p-6 max-w-md">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-500/20 rounded-full mb-4">
            <Lock className="text-amber-400" size={32} />
          </div>
          
          {/* Feature Title */}
          <h4 className="text-white font-bold text-xl mb-3">
            🔒 {displayTitle}
          </h4>
          
          {/* Feature Description */}
          <p className="text-white/80 text-sm mb-5 leading-relaxed">
            {displayDescription}
          </p>
          
          {/* Upgrade CTA */}
          <button
            onClick={() => navigate('/pricing')}
            className="bg-gradient-to-r from-[#FF9F0A] to-[#FFB84D] text-[#2D0C4B] font-bold px-6 py-3 rounded-lg text-base hover:opacity-90 transition-opacity shadow-lg"
          >
            <Sparkles className="inline mr-2" size={18} />
            {t('upgradeForAccess')}
          </button>
          
          <p className="text-white/50 text-xs mt-3">
            {t('orTryFree24h')}
          </p>
        </div>
      </div>
    </div>
  );
};

// Card component that shows locked state for feature cards
export const FeatureCard = ({ 
  featureId, 
  title, 
  description, 
  icon: Icon, 
  onClick, 
  color = '#6B3FA0',
  children 
}) => {
  const { hasFeatureAccess } = useAccess();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const hasAccess = hasFeatureAccess(featureId);
  
  const handleClick = () => {
    if (hasAccess && onClick) {
      onClick();
    } else if (!hasAccess) {
      navigate('/pricing');
    }
  };
  
  return (
    <div
      onClick={handleClick}
      className={`relative bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 cursor-pointer
        ${hasAccess 
          ? 'hover:shadow-xl hover:scale-[1.02]' 
          : 'opacity-75 hover:opacity-90'
        }`}
      style={{ borderTop: `4px solid ${hasAccess ? color : '#9CA3AF'}` }}
    >
      {/* Locked badge */}
      {!hasAccess && (
        <div className="absolute top-3 right-3 z-10">
          <LockedBadge />
        </div>
      )}
      
      <div className={`p-6 ${!hasAccess ? 'opacity-60' : ''}`}>
        {Icon && (
          <div 
            className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
            style={{ backgroundColor: `${hasAccess ? color : '#9CA3AF'}20` }}
          >
            <Icon size={24} style={{ color: hasAccess ? color : '#9CA3AF' }} />
          </div>
        )}
        
        <h3 className="font-bold text-lg text-gray-900 mb-2">{title}</h3>
        
        {description && (
          <p className="text-gray-600 text-sm">{description}</p>
        )}
        
        {children}
      </div>
      
      {/* Upgrade prompt for locked features */}
      {!hasAccess && (
        <div className="px-6 pb-4">
          <div className="flex items-center gap-2 text-amber-600 text-sm">
            <Sparkles size={14} />
            <span>{t('upgradeToAccess')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Access status banner
export const AccessBanner = () => {
  const { access, loading } = useAccess();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  if (loading || !access) return null;
  
  const bannerConfig = {
    admin: {
      bg: 'bg-gradient-to-r from-purple-600 to-indigo-600',
      textKey: 'adminFullAccess',
      icon: '👑',
      showUpgrade: false
    },
    trial: {
      bg: 'bg-gradient-to-r from-blue-500 to-cyan-500',
      textKey: 'trialPeriod',
      icon: '⏱️',
      suffix: access.hours_left ? `${Math.round(access.hours_left)}h ${t('hoursRemaining')}` : '24h',
      showUpgrade: true
    },
    free_access: {
      bg: 'bg-gradient-to-r from-green-500 to-emerald-500',
      textKey: 'freeAccess',
      icon: '🎁',
      showUpgrade: false
    },
    subscriber: {
      bg: 'bg-gradient-to-r from-amber-500 to-orange-500',
      textKey: 'subscriber',
      icon: '✨',
      suffix: access.package_id,
      showUpgrade: access.features?.length < 7
    },
    expired: {
      bg: 'bg-gradient-to-r from-red-500 to-rose-500',
      textKey: 'subscriptionExpired',
      icon: '⚠️',
      showUpgrade: true
    },
    none: {
      bg: 'bg-gradient-to-r from-gray-500 to-gray-600',
      textKey: 'noActiveSubscription',
      icon: '🔒',
      showUpgrade: true
    }
  };
  
  const config = bannerConfig[access.access_type] || bannerConfig.none;
  const displayText = `${config.icon} ${t(config.textKey)}${config.suffix ? ` - ${config.suffix}` : ''}`;
  
  return (
    <div className={`${config.bg} text-white py-2 px-4`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <span className="text-sm font-medium">{displayText}</span>
        {config.showUpgrade && (
          <button
            onClick={() => navigate('/pricing')}
            className="bg-white/20 hover:bg-white/30 text-white text-xs px-3 py-1 rounded-full transition-colors"
          >
            {t('upgrade')}
          </button>
        )}
      </div>
    </div>
  );
};

export default FeatureGate;
